'use client';

// # Reminder Detail Dialog Component
// Dialog for viewing and managing reminder details

import { useState, useEffect } from 'react';
import { X, Calendar, Clock, Flag, FileText, Building2, CheckCircle2, Trash2, BellRing, ExternalLink, Edit, Loader2, Share2 } from 'lucide-react';
import { useAuth } from '../../auth';
import { useDashboard } from '../../lib/dashboard-context';
import { ReminderService, type Reminder } from '../../lib/reminder-service';
import { TaskDetailDialog } from './TaskDetailDialog';
import { CrucialSelector } from '../tasks/CrucialSelector';
import { ShareDialog } from '../share/ShareDialog';

interface ReminderDetailDialogProps {
  reminderId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function ReminderDetailDialog({
  reminderId,
  isOpen,
  onClose,
  onUpdate
}: ReminderDetailDialogProps) {
  const { user } = useAuth();
  const { currentDepartment } = useDashboard();
  
  const [reminder, setReminder] = useState<Reminder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [editingRemindDate, setEditingRemindDate] = useState('');
  const [editingRemindTime, setEditingRemindTime] = useState('');
  const [editingPriority, setEditingPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [editingCategory, setEditingCategory] = useState<'general' | 'task' | 'meeting' | 'follow_up' | 'deadline' | 'crm' | 'presentation' | 'project' | 'personal' | 'other'>('general');
  const [editingIsCrucial, setEditingIsCrucial] = useState(false);
  const [editingCrucialForType, setEditingCrucialForType] = useState<'milestone' | 'sales' | 'budget' | 'presentation' | null>(null);
  const [editingCrucialForId, setEditingCrucialForId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Share dialog
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Load reminder details
  useEffect(() => {
    if (isOpen && reminderId) {
      loadReminder();
      setIsEditing(false);
    }
  }, [isOpen, reminderId]);

  // Initialize editing state when reminder loads
  useEffect(() => {
    if (reminder) {
      setEditingTitle(reminder.title);
      setEditingDescription(reminder.description || '');
      const remindDate = new Date(reminder.remind_at);
      setEditingRemindDate(remindDate.toISOString().split('T')[0]);
      setEditingRemindTime(remindDate.toTimeString().slice(0, 5));
      setEditingPriority(reminder.priority);
      setEditingCategory(reminder.category);
      setEditingIsCrucial(reminder.is_crucial || false);
      setEditingCrucialForType(reminder.crucial_for_type || null);
      setEditingCrucialForId(reminder.crucial_for_id || null);
    }
  }, [reminder]);

  const loadReminder = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get reminder by ID
      const reminder = await ReminderService.getReminder(reminderId);
      
      if (reminder) {
        setReminder(reminder);
      } else {
        setError('Reminder not found');
      }
    } catch (err) {
      console.error('Error loading reminder:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!reminderId) return;
    
    try {
      setCompleting(true);
      const success = await ReminderService.completeReminder(reminderId);
      if (success) {
        await loadReminder();
        onUpdate?.();
      }
    } catch (err) {
      console.error('Error completing reminder:', err);
      setError(err instanceof Error ? err.message : 'Failed to complete reminder');
    } finally {
      setCompleting(false);
    }
  };

  const handleDelete = async () => {
    if (!reminderId || !confirm('Are you sure you want to delete this reminder? This action cannot be undone.')) return;
    
    try {
      setDeleting(true);
      setError(null);
      
      const success = await ReminderService.deleteReminder(reminderId);
      
      if (success) {
        onUpdate?.();
        onClose();
      } else {
        setError('Failed to delete reminder. Please try again.');
        setDeleting(false);
      }
    } catch (err) {
      console.error('Error deleting reminder:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete reminder');
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (!reminderId || !editingTitle.trim() || !editingRemindDate) {
      setError('Title and date are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Combine date and time
      const remindAt = editingRemindTime 
        ? `${editingRemindDate}T${editingRemindTime}:00`
        : `${editingRemindDate}T09:00:00`;

      const updated = await ReminderService.updateReminder(reminderId, {
        title: editingTitle.trim(),
        description: editingDescription.trim() || undefined,
        remind_at: remindAt,
        priority: editingPriority,
        category: editingCategory,
        is_crucial: editingIsCrucial,
        crucial_for_type: editingCrucialForType,
        crucial_for_id: editingCrucialForId
      });

      if (updated) {
        setReminder(updated);
        setIsEditing(false);
        onUpdate?.();
      }
    } catch (err) {
      console.error('Error updating reminder:', err);
      setError(err instanceof Error ? err.message : 'Failed to update reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (reminder) {
      setEditingTitle(reminder.title);
      setEditingDescription(reminder.description || '');
      const remindDate = new Date(reminder.remind_at);
      setEditingRemindDate(remindDate.toISOString().split('T')[0]);
      setEditingRemindTime(remindDate.toTimeString().slice(0, 5));
      setEditingPriority(reminder.priority);
      setEditingCategory(reminder.category);
      setEditingIsCrucial(reminder.is_crucial || false);
      setEditingCrucialForType(reminder.crucial_for_type || null);
      setEditingCrucialForId(reminder.crucial_for_id || null);
    }
    setIsEditing(false);
    setError(null);
  };

  if (!isOpen) return null;

  const remindDate = reminder ? new Date(reminder.remind_at) : null;
  const isOverdue = remindDate && remindDate < new Date() && !reminder?.is_completed;
  const daysUntil = remindDate ? Math.ceil((remindDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 0;

  const getPriorityColor = (priority: Reminder['priority']) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 p-2 sm:p-4 overflow-hidden">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 min-w-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <BellRing className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Reminder Details</h2>
                {reminder && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                    {reminder.is_completed ? 'Completed' : isOverdue ? 'Overdue' : daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isEditing && reminder && !reminder.is_completed && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={completing || deleting}
                  title="Edit reminder"
                >
                  <Edit className="w-5 h-5 text-gray-500" />
                </button>
              )}
              {reminder && (
                <button
                  onClick={() => setShowShareDialog(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={completing || deleting || saving}
                  title="Share reminder"
                >
                  <Share2 className="w-5 h-5 text-gray-500" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={completing || deleting || saving}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="px-4 sm:px-6 py-3 bg-red-50 border-b border-red-200">
              <div className="flex items-center gap-2 text-red-700 text-sm">
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : reminder ? (
              <div className="space-y-6 min-w-0">
                {isEditing ? (
                  <>
                    {/* Edit Form */}
                    <div className="space-y-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          placeholder="Enter reminder title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          disabled={saving}
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          Description
                        </label>
                        <textarea
                          value={editingDescription}
                          onChange={(e) => setEditingDescription(e.target.value)}
                          placeholder="Optional description"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                          disabled={saving}
                        />
                      </div>

                      {/* Date and Time */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={editingRemindDate}
                            onChange={(e) => setEditingRemindDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={saving}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Clock className="w-4 h-4 inline mr-1" />
                            Time
                          </label>
                          <input
                            type="time"
                            value={editingRemindTime}
                            onChange={(e) => setEditingRemindTime(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={saving}
                          />
                        </div>
                      </div>

                      {/* Priority and Category */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <Flag className="w-4 h-4 inline mr-1" />
                            Priority
                          </label>
                          <select
                            value={editingPriority}
                            onChange={(e) => setEditingPriority(e.target.value as any)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={saving}
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="urgent">Urgent</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            <FileText className="w-4 h-4 inline mr-1" />
                            Category
                          </label>
                          <select
                            value={editingCategory}
                            onChange={(e) => setEditingCategory(e.target.value as any)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            disabled={saving}
                          >
                            <option value="general">General</option>
                            <option value="task">Task</option>
                            <option value="meeting">Meeting</option>
                            <option value="follow_up">Follow Up</option>
                            <option value="deadline">Deadline</option>
                            <option value="crm">CRM</option>
                            <option value="presentation">Presentation</option>
                            <option value="project">Project</option>
                            <option value="personal">Personal</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      {/* Crucial Selector */}
                      <CrucialSelector
                        isCrucial={editingIsCrucial}
                        crucialForType={editingCrucialForType}
                        crucialForId={editingCrucialForId}
                        departmentId={currentDepartment?.department_id}
                        onChange={(crucial, type, id) => {
                          setEditingIsCrucial(crucial);
                          setEditingCrucialForType(type);
                          setEditingCrucialForId(id);
                        }}
                        disabled={saving}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    {/* View Mode */}
                    {/* Title */}
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{reminder.title}</h3>
                      {reminder.description && (
                        <p className="text-gray-600">{reminder.description}</p>
                      )}
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <Calendar className="w-4 h-4" />
                          <span>Remind At</span>
                        </div>
                        <p className="font-semibold text-gray-900">
                          {remindDate?.toLocaleDateString()} {remindDate?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <Flag className="w-4 h-4" />
                          <span>Priority</span>
                        </div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(reminder.priority)}`}>
                          {reminder.priority}
                        </span>
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <FileText className="w-4 h-4" />
                          <span>Category</span>
                        </div>
                        <p className="font-semibold text-gray-900 capitalize">{reminder.category}</p>
                      </div>

                      {currentDepartment && (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                            <Building2 className="w-4 h-4" />
                            <span>Department</span>
                          </div>
                          <p className="font-semibold text-gray-900">{currentDepartment.department_display_name}</p>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Linked Task */}
                {reminder.related_entity_type === 'task' && reminder.related_entity_id && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-blue-900">Linked to Task</span>
                      </div>
                      <button
                        onClick={() => setShowTaskDialog(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Task
                      </button>
                    </div>
                  </div>
                )}

                {/* Status */}
                {reminder.is_completed && reminder.completed_at && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="font-medium">Completed on {new Date(reminder.completed_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 flex-wrap">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleCancelEdit}
                        disabled={saving}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving || !editingTitle.trim() || !editingRemindDate}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      {!reminder.is_completed && (
                        <button
                          onClick={handleComplete}
                          disabled={completing}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {completing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Completing...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              Mark as Completed
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={handleDelete}
                        disabled={deleting}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {deleting ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Task Dialog */}
      {reminder?.related_entity_type === 'task' && reminder.related_entity_id && (
        <TaskDetailDialog
          taskId={reminder.related_entity_id}
          isOpen={showTaskDialog}
          onClose={() => setShowTaskDialog(false)}
          onUpdate={() => {
            loadReminder();
            onUpdate?.();
          }}
        />
      )}
      
      {/* Share Dialog */}
      {showShareDialog && reminder && (
        <ShareDialog
          isOpen={showShareDialog}
          onClose={() => setShowShareDialog(false)}
          itemType="reminder"
          itemId={reminderId}
          itemTitle={reminder.title}
        />
      )}
    </>
  );
}

